import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "react-query";
import axios from "../../config/axiosInstance";
import { USERS } from "../../config/apiConfig";
import DrawerLoading from "../DrawerLoading";
import ForbiddenQueriesPermission from "../common/ForbiddenQueriesPermission";
import { FaSearch, FaSearchDollar, FaTools } from "react-icons/fa";
import { motion } from "framer-motion";
import { Tooltip } from "react-tooltip";
import CadastralPFQuery from "../queries/CadastralPFQuery";
import CadastralPJQuery from "../queries/CadastralPJQuery";
import CustomCadastralQuery from "../queries/CustomCadastralQuery";
import { formatToReal } from "../../utils/utils";
import { useApplication } from "../../contexts/ApplicationContext";

const QueriesCadastralTable = () => {
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [selectedPrice, setSelectedPrice] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [isListVisible, setIsListVisible] = useState(true);
  const componentRef = useRef(null);

  const { isLoading, data: userQueryPermissions } = useQuery(
    ["userQueryPermissions"],
    async () => {
      const { data: items } = await axios.get(USERS.GET_USER_QUERY_PERMISSIONS);
      return items.data.filter(
        (query) =>
          query.categoryId === 2 && query.status != -1 && !query.isModular
      );
    }
  );

  const filteredQueries = userQueryPermissions?.filter((query) =>
    query?.name?.toLowerCase().includes(searchTerm?.toLowerCase())
  );

  const handleQueryClick = (query) => {
    setSelectedQuery(query);
    setSelectedPrice(query.price);
    setIsListVisible(false);
    setTimeout(() => {
      componentRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 200);
  };

  const handleBackClick = () => {
    setIsListVisible(true);
    setSelectedQuery(null);
  };

  const QueryCard = ({ query, index }) => {
    const { user } = useApplication();
    const titleRef = useRef(null);
    const [isTruncated, setIsTruncated] = useState(false);

    useEffect(() => {
      const el = titleRef.current;
      if (el) {
        requestAnimationFrame(() => {
          setIsTruncated(el.scrollWidth > el.clientWidth);
        });
      }
    }, [query.name]);

    return (
      <>
        {query?.status == 1 ? (
          <motion.div
            key={query.id}
            initial={{ opacity: 0, scale: 0.97, y: 6 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.35, delay: index * 0.04 }}
            onClick={() => handleQueryClick(query)}
            className="relative group cursor-pointer rounded-lg border border-gray-200 bg-gradient-to-r from-gray-50 to-white p-4 shadow-sm transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
          >
            {/* Botão flutuante fora do card */}
            <div className="absolute -top-3 right-3 z-20 opacity-0 group-hover:opacity-100 transition-all duration-300 scale-90 group-hover:scale-100">
              <div className="bg-gradient-to-r from-blue-500 to-green-400 text-white text-[11px] font-semibold px-3 py-[2px] rounded-full shadow">
                CONSULTAR AGORA
              </div>
            </div>

            {/* Ícone decorativo */}
            <FaSearchDollar className="absolute bottom-2 right-2 text-gray-200 text-4xl z-0 group-hover:text-green-300 transition-colors duration-300" />

            {/* Conteúdo */}
            <div className="relative z-10 flex flex-col gap-1">
              <h3
                className="truncate text-sm font-semibold text-gray-900"
                title={query.name}
              >
                {query.name?.toUpperCase()}
              </h3>
              <span className="text-[11px] text-gray-500">
                {user?.Company?.showPriceQuery && query?.price
                  ? `${formatToReal(query?.price)}  - `
                  : ""}{" "}
                Consulta cadastral
              </span>
            </div>

            {/* Faixa inferior */}
            <div className="absolute bottom-0 left-0 h-[2px] w-full bg-gradient-to-r from-blue-500 to-green-400 transition-all duration-300 opacity-40 group-hover:opacity-80 rounded-b-lg" />
          </motion.div>
        ) : (
          <motion.div
            key={query.id}
            initial={{ opacity: 0, scale: 0.97, y: 6 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.35, delay: index * 0.04 }}
            className="bg-gradient-to-r from-gray-100 to-gray-200 border border-gray-300 rounded-lg p-4 shadow-sm cursor-not-allowed relative group overflow-hidden opacity-50 pointer-events-none"
          >
            {/* Conteúdo principal */}
            <div className="flex flex-col gap-1 relative z-10">
              <div className="flex items-center gap-2">
                <FaTools className="text-gray-500 text-sm" />
                <h3
                  className="text-sm font-semibold text-gray-600 truncate"
                  title={query.name}
                >
                  {query.name?.toUpperCase()}
                </h3>
              </div>
              <span className="text-[11px] text-gray-500">
                Consulta em manutenção
              </span>
            </div>

            {/* Ícone decorativo no canto */}
            <FaTools className="absolute bottom-2 right-2 text-gray-300 text-4xl z-0" />

            {/* Faixa inferior desativada */}
            <div className="absolute bottom-0 left-0 h-[2px] w-full bg-gray-400 rounded-b-lg z-0" />

            {/* Hover: painel de manutenção */}
            <div className="absolute bottom-0 left-0 w-full overflow-hidden transition-all duration-300 ease-in-out h-0 group-hover:h-8 z-10">
              <div className="flex items-center justify-center bg-gray-400 text-white text-[11px] font-semibold h-8 opacity-100 transition-opacity duration-300 rounded-b-lg shadow-inner">
                EM MANUTENÇÃO
              </div>
            </div>
          </motion.div>
        )}
      </>
    );
  };

  const componentsMap = {
    48: (
      <CadastralPFQuery query={selectedQuery} selectedPrice={selectedPrice} />
    ),
    49: (
      <CadastralPJQuery query={selectedQuery} selectedPrice={selectedPrice} />
    ),
  };

  if (isLoading) return <DrawerLoading />;
  if (!userQueryPermissions?.length) return <ForbiddenQueriesPermission />;

  return (
    <div className="w-full px-4 md:px-12 pt-4 min-h-screen">
      <div className="mb-5">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
          Consultas Cadastrais
        </h1>
        <div className="w-16 h-[3px] bg-gradient-to-r from-blue-600 to-green-400 mt-2 rounded" />
        <p className="text-sm sm:text-base text-gray-600 mt-3">
          Aqui você encontra todas as modalidades de consultas cadastrais
          disponíveis para você, com informações detalhadas para decisões
          estratégicas.
        </p>
      </div>

      {isListVisible ? (
        <>
          <div className="mb-3 relative">
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar consultas cadastrais..."
                className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 shadow-sm focus:ring-2 focus:ring-blue-600 focus:outline-none text-sm sm:text-base placeholder-gray-400 transition-all"
              />
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-700 pointer-events-none">
                <FaSearch size={18} />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4 mb-6 bg-gray-50 rounded-md p-4 shadow-sm border border-gray-200">
            {filteredQueries?.map((query, index) => (
              <QueryCard query={query} index={index} key={query.id} />
            ))}
          </div>
        </>
      ) : (
        <div ref={componentRef}>
          <button
            onClick={handleBackClick}
            className="flex items-center text-sm text-green-700 hover:text-green-900 font-medium mb-4 transition-all"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Voltar para a listagem
          </button>

          {selectedQuery?.queryCustomizedId ? (
            <CustomCadastralQuery
              query={selectedQuery}
              selectedPrice={selectedPrice}
            />
          ) : (
            componentsMap[selectedQuery.id] || (
              <div className="text-gray-500 text-center mt-4">
                Componente não encontrado para esta consulta.
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default QueriesCadastralTable;
